import React from 'react';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses, MenuItemStyles } from 'react-pro-sidebar';
import { Switch } from './components/Switch';
import { SidebarHeader } from './components/SidebarHeader';
import { SidebarFooter } from './components/SidebarFooter';
import { SidebarContent } from './components/SidebarContent';

type Theme = 'light' | 'dark';

const themes = {
  light: {
    sidebar: {
      backgroundColor: '#ffffff',
      color: '#000000',
    },
    menu: {
      menuContent: '#fbfcfd',
      icon: '#0098e5',
      hover: {
        backgroundColor: '#c5e4ff',
        color: '#44596e',
      },
      disabled: {
        color: '#9fb6cf',
      },
    },
  },
  dark: {
    sidebar: {
      backgroundColor: '#0b2948',
      color: '#8ba1b7',
    },
    menu: {
      menuContent: '#082440',
      icon: '#59d0ff',
      hover: {
        backgroundColor: '#00458b',
        color: '#b6c8d9',
      },
      disabled: {
        color: '#3e5e7e',
      },
    },
  },
};

// hex to rgba converter
const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface ToggleButtonStyleProps {
  collapsed: boolean;
}

// This function now returns the style object based on the collapsed state
// This function now explicitly returns CSSProperties
const ToggleButton: React.FC<{ collapsed: boolean; onClick: () => void }> = ({ collapsed, onClick }) => {
  const circleStyle: React.CSSProperties = {
    cursor: 'pointer',
    position: 'absolute',
    right: '-1px', // Positioning the circle relative to the sidebar's edge
    top: 'calc(50% - 10px)', // Vertically centering
    width: '20px', // Circle size
    height: '20px', // Circle size
    borderRadius: '50%', // Making the circle
    backgroundColor: 'rgb(35, 47, 62)', // Circle color
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000, // Ensuring it's placed above other elements
  };

  // Determine the appropriate transformation based on the sidebar's state
  const svgTransform = collapsed ? 'translate(1px, 0)' : 'translate(-1px, 0)';

  return (
    <div style={circleStyle} onClick={onClick}>
      <svg
        viewBox='0 0 10 10'
        width='12'
        height='12'
        style={{ transform: svgTransform, transition: 'transform 200ms ease' }} // Adding conditional transformation
      >
            {collapsed ? (
          // Arrow points right when collapsed
          <path d="M 3,2 L 6,5 L 3,8" stroke="white" fill="none" strokeWidth="1.5"/>
        ) : (
          // Arrow points left when expanded
          <path d="M 7,2 L 4,5 L 7,8" stroke="white" fill="none" strokeWidth="1.5"/>
        )}
      </svg>
    </div>
  );
};

const sidebarContainerStyle: React.CSSProperties = {
  position: 'relative',
  height: '100vh', // Full viewport height
  display: 'flex',
  flexDirection: 'column',
};

const sidebarContentStyle: React.CSSProperties = {
  flex: 1, // Allows it to grow and fill available space
  overflowY: 'auto', // In case content overflows vertically, allow scrolling

};

// Ensure the Sidebar content is scrollable
const sidebarStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%', // Take full height of its container
  overflowY: 'auto', // Enable vertical scrolling

};
export const AppSideBar: React.FC = () => {
  const [collapsed, setCollapsed] = React.useState(false);
  const [toggled, setToggled] = React.useState(false);
  const [broken, setBroken] = React.useState(false);
  const [rtl, setRtl] = React.useState(false);
  const [hasImage, setHasImage] = React.useState(false);
  const [theme, setTheme] = React.useState<Theme>('light');

  const handleToggleSidebar = () => {
    setCollapsed(!collapsed);
  };
  // handle on RTL change event
  const handleRTLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRtl(e.target.checked);
  };

  // handle on theme change event
  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTheme(e.target.checked ? 'dark' : 'light');
  };

  // handle on image change event
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasImage(e.target.checked);
  };

  const menuItemStyles: MenuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: themes[theme].menu.icon,
      [`&.${menuClasses.disabled}`]: {
        color: themes[theme].menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRgba(themes[theme].menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
          : 'transparent',
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: themes[theme].menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRgba(themes[theme].menu.hover.backgroundColor, hasImage ? 0.8 : 1),
        color: themes[theme].menu.hover.color,
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  return (
    // <div style={{ display: 'flex', height: '100%', direction: rtl ? 'rtl' : 'ltr' }}>
    <div style={{ display: 'flex', height: '100vh', flexDirection: 'row', position: 'relative' }}>
      <div style={sidebarContainerStyle}> {/* Apply styles to the sidebar container */}

        <Sidebar
          collapsed={collapsed}
          toggled={toggled}
          onBackdropClick={() => setToggled(false)}
          onBreakPoint={setBroken}
          rtl={rtl}
          breakPoint='md'
          backgroundColor={hexToRgba(themes[theme].sidebar.backgroundColor, hasImage ? 0.9 : 1)}
          rootStyles={{
            color: themes[theme].sidebar.color,
          }}
          style={sidebarStyle}
        >

          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SidebarHeader rtl={rtl} collapsed={collapsed}  />
            <div style={sidebarContentStyle}  className="custom-scrollbar">
              {/* This div wraps the content and allows it to scroll, pushing the Footer down */}
              <SidebarContent collapsed={collapsed} hasImage={hasImage} />
            </div>
            <SidebarFooter collapsed={collapsed} />
          </div>
        </Sidebar>
        <ToggleButton collapsed={collapsed} onClick={handleToggleSidebar} />

      </div>

    </div>
  );
};